import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, Headphones, Mic, Send, X } from "lucide-react";
import { useTeam } from "@/lib/team";
import { useAuth } from "@/lib/auth";
import { workSpeech } from "@/lib/due";
import { AFFILIATE_LINKS } from "@/constants/affiliateLinks";
import { GUIDE, STARTERS, WALK_TOPICS } from "@/edgar/guide";
import { WALKS } from "@/edgar/walks";
import WalkBar from "@/edgar/WalkBar";
import { POCKET_IDLE_MS, beep, itemName, keepAwake } from "@/edgar/pocket";
import { useEdgarPrefs } from "@/edgar/prefs";
import EdgarButton from "@/edgar/EdgarButton";
import WakeMic from "@/edgar/WakeMic";
import { rank } from "@/edgar/match";
import { askPrice, command, hush, listenOnce, priceSpeech, resultSpeech, speak, speechQueue, statusSpeech, voiceSupported, wakeListener } from "@/edgar/voice";
import { ASK_FIRST, isNo, isYes, lastKeepPath, parseAction, rememberKeep } from "@/edgar/actions";
import { keepersChanged } from "@/pages/worth/KeepersBar";
import { buyPack } from "@/edgar/packs";
import { API, api } from "@/lib/api";
import { DEFAULT_FLIP, flipMath, shipCost } from "@/lib/flip";
import VoiceSettings from "@/edgar/VoiceSettings";
import { PAGE_TIPS, SHOW, highlight } from "@/edgar/show";

const KEY = "snaplist.edgarChat";
const RESEARCH_HIDDEN = ["/", "/library", "/listings", "/dashboard", "/queue", "/team", "/extension"];
const HELLO = { from: "edgar", text: "Hi, I'm Edgar, your SnapList guide. Ask me how to do anything in the app and I'll point you to the right screen." };

const Avatar = ({ size = "h-8 w-8" }) => (
  <span className={`${size} inline-flex shrink-0 items-center justify-center rounded-full bg-[#0053DA] font-serif text-sm font-bold italic text-white`} aria-hidden>E</span>
);

// Edgar: free, built-in helper that answers from the guide and opens the right screen.
export default function Edgar() {
  const team = useTeam();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [text, setText] = useState("");
  const [chat, setChat] = useState(() => { try { return (JSON.parse(sessionStorage.getItem(KEY)) || [HELLO]).map((m) => ({ ...m, confirm: false, thinking: false })); } catch { return [HELLO]; } });
  const end = useRef(null);
  const topics = useMemo(() => [...WALK_TOPICS, ...GUIDE].filter((t) => (t.role === "owner" ? !team.isAssistant : t.role === "assistant" ? team.isAssistant : t.role === "photos" ? team.canPhotos : true)
    && !(team.researchOnly && RESEARCH_HIDDEN.includes(t.path))), [team.isAssistant, team.researchOnly, team.canPhotos]);
  useEffect(() => { sessionStorage.setItem(KEY, JSON.stringify(chat.slice(-40))); const box = end.current?.parentElement; if (box) box.scrollTop = box.scrollHeight; }, [chat, open]);

  const answer = (t) => ({ from: "edgar", text: t.a, id: t.id });
  const notSure = () => ({ from: "edgar", text: "I'm not sure about that one yet. Here are things I can help with:", more: STARTERS.filter((id) => topics.some((t) => t.id === id)) });
  // Free guide first (unless it's a follow-up); otherwise stream a conversational AI answer that remembers the chat.
  const pending = useRef(null);
  const runAction = async (act) => {
    try {
      if (act.kind === "batch") {
        sessionStorage.setItem("snaplist.edgarBatchName", act.name);
        visit("/batch", null, ["batch-name-input", "batch-builder"]);
        setTimeout(() => window.dispatchEvent(new Event("snaplist:batch-name")), 500);
        return act.name ? `Here's a new batch called ${act.name}. Add your photos and tap Create batch.` : "Here's a new batch. Give it a name, add your photos and tap Create batch.";
      }
      if (act.kind === "search") {
        sessionStorage.setItem("snaplist.edgarSearch", act.q);
        visit("/library", null, ["library-search"]);
        setTimeout(() => window.dispatchEvent(new Event("snaplist:library-search")), 500);
        return `Searching your library for ${act.q}.`;
      }
      if (act.kind === "open" && act.to === "keepers") {
        const { data } = await api.get("/keepers/today", { params: { tz_offset: new Date().getTimezoneOffset() } });
        if (data.batch_id) { navigate(`/batch?b=${data.batch_id}`); return "Here are today's keepers."; }
        visit("/research", "single", ["research-upload-button"]);
        return "You haven't kept anything today yet. Here's Research so you can find some.";
      }
      if (act.kind === "open" && act.to === "scan") { researchNow("scan"); return "Opening the barcode scanner."; }
      if (act.kind === "lookup") {
        if (act.mode === "single") { visit("/research", "single", ["research-upload-button", "research-scan-barcode-button"]); return "Here's Research. Snap a photo, scan the barcode or say the title."; }
        visit("/research", act.mode, []);
        return act.mode === "media" ? "Here's Media batch research. Scan or type each one and I'll price them all." : "Here's What's it worth. Add each item and I'll price them all.";
      }
      if (act.kind === "open") { visit(act.to, act.mode, []); return `Opening ${act.what.replace(/^(my|the) /, "")}.`; }
      if (act.kind === "undo") {
        const path = lastKeepPath();
        if (!path) return "I don't see a keep to undo yet.";
        await api.delete(path);
        rememberKeep(null);
        keepersChanged();
        return "Done. I took it back out of Keepers.";
      }
      if (act.kind === "work") {
        if (!team.isAssistant) return "Assignments are for your assistants. Open your team page to see who's working on what.";
        const { data } = await api.get("/my-assignments", { params: { tz_offset: new Date().getTimezoneOffset() } });
        return workSpeech(data, (user?.name || "").split(" ")[0]);
      }
      if (act.kind === "funding") {
        visit("/tools", null, ["tool-card-0"]);
        return `Here are ways to stretch your buying money. ${AFFILIATE_LINKS.map((l) => l.edgar || `${l.name}: ${l.blurb}`).join(" ")} I opened Recommended tools so you can tap the links.`;
      }
      if (act.kind === "sync") { await api.post("/ebay/sync-listings"); return "Synced with eBay. Your listing statuses are up to date."; }
      if (act.kind === "clear") { await api.delete("/posting/reports"); return "All clear. Your Whatnot alerts are gone."; }
    } catch (e) {
      return act.kind === "sync" ? "I couldn't reach eBay. Check that your eBay account is connected." : "Hmm, that didn't work. Try it from the screen instead.";
    }
    return "Done.";
  };
  const doAction = async (asked, act) => {
    setChat((c) => [...c, { from: "you", text: asked }]);
    if (act.confirm) {
      pending.current = act;
      const reply = { from: "edgar", text: ASK_FIRST[act.kind], confirm: true };
      setChat((c) => [...c, reply]);
      return reply;
    }
    const reply = { from: "edgar", text: await runAction(act), done: true };
    setChat((c) => [...c.map((m) => (m.confirm ? { ...m, confirm: false } : m)), reply]);
    return reply;
  };
  const ask = async (q, opts = {}) => {
    const asked = q.trim();
    if (!asked) return null;
    setText("");
    if (pending.current) {
      const act = pending.current;
      pending.current = null;
      if (isYes(asked)) return doAction(asked, { ...act, confirm: false });
      if (isNo(asked)) {
        const reply = { from: "edgar", text: "Okay, I won't." };
        setChat((c) => [...c.map((m) => (m.confirm ? { ...m, confirm: false } : m)), { from: "you", text: asked }, reply]);
        return reply;
      }
      setChat((c) => c.map((m) => (m.confirm ? { ...m, confirm: false } : m)));
    }
    const act = parseAction(asked);
    if (act) return doAction(asked, act);
    const hits = rank(asked, topics);
    const best = hits[0]?.s >= 2 ? hits[0].t : null;
    const followUp = chat.length > 2 && /\b(that|it|this|those|them|then|also|instead|what about|why|and)\b/i.test(asked) && (hits[0]?.s || 0) < 4;
    if (best && !followUp) {
      const reply = { ...answer(best), more: hits.slice(1, 3).map((h) => h.t.id) };
      setChat((c) => [...c, { from: "you", text: asked }, reply]);
      return reply;
    }
    setChat((c) => [...c, { from: "you", text: asked }, { from: "edgar", text: "", thinking: true, ai: true }]);
    const talk = opts.voice && (live.current.prefs.on || live.current.pocket);
    const sq = talk ? speechQueue(opts.onSpoken) : null;
    const show = (t, done, extra = {}) => setChat((c) => { const n = [...c]; const i = n.length - 1; n[i] = { ...n[i], text: t, thinking: !done && !t, ...extra }; return n; });
    let full = "";
    let spoken = 0;
    try {
      const history = chat.slice(-12).filter((m) => m.text && !m.thinking).map((m) => `${m.from === "you" ? "User" : "Edgar"}: ${m.text}`);
      const res = await fetch(`${API}/edgar/ask`, { method: "POST", credentials: "include", headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ question: asked, page: pathname, history, topics: topics.map(({ id, q: tq, a }) => ({ id, q: tq, a })) }) });
      if (!res.ok) throw Object.assign(new Error("ask"), { status: res.status });
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        full += dec.decode(value, { stream: true });
        const clean = full.replace(/\[\[[^\]]*\]?\]?\s*$/, "").trim();
        show(clean, false);
        if (sq && !spoken) {
          const m = clean.match(/^.+?[.!?](?=\s)/);
          if (m) { sq.add(m[0]); spoken = m[0].length; }
        }
      }
    } catch (e) {
      const reply = e?.status === 429
        ? { ...notSure(), buy: !team.isAssistant, text: team.isAssistant
          ? "You've used this month's smart answers. Ask the account owner to add more. Until then I'll use my built-in guide:"
          : "You've used this month's smart answers. Add 100 more, or I'll use my built-in guide until next month:" }
        : notSure();
      setChat((c) => [...c.filter((m) => !m.thinking && !(m.ai && !m.text)), reply]);
      sq?.end();
      return reply;
    }
    const topic = full.match(/\[\[topic:([\w-]+)\]\]/)?.[1];
    const id = topics.some((t) => t.id === topic) ? topic : undefined;
    const clean = full.replace(/\[\[[^\]]*\]\]/g, "").trim() || "Hmm, I lost my train of thought. Ask me that again?";
    show(clean, true, id ? { id } : {});
    if (sq) { sq.add(clean.slice(spoken)); sq.end(); }
    return { from: "edgar", text: clean, id, ai: true, spoken: !!sq };
  };
  const pick = (id) => { const t = topics.find((x) => x.id === id); if (t) setChat((c) => [...c, { from: "you", text: t.q }, answer(t)]); };
  const visit = (path, mode, show) => {
    if (mode) { localStorage.setItem("snaplist.researchMode", mode); window.dispatchEvent(new CustomEvent("snaplist:research-mode", { detail: mode })); }
    if (pathname !== path) navigate(path);
    setTimeout(() => highlight(show || []), 400);
  };
  const [walk, setWalk] = useState(null);
  const stepTo = (w, i) => {
    const s = WALKS[w].steps[i];
    setWalk({ id: w, i });
    visit(s.path, s.mode, s.show);
    if (live.current.prefs?.on) speak(s.text, () => live.current.prefs.loop && setTimeout(listen, 250));
  };
  const endWalk = (done) => { setWalk(null); hush(); if (done) setChat((c) => [...c, { from: "edgar", text: "Nice work! Ask me anything else whenever you need." }]); };
  const go = (t) => {
    setOpen(false);
    if (t.walk) { stepTo(t.walk, 0); return; }
    visit(t.path, t.mode, SHOW[t.id]);
  };
  const researchNow = (kind) => {
    setOpen(false);
    sessionStorage.setItem("snaplist.edgarCapture", kind);
    visit("/research", "single", kind === "scan" ? [] : ["research-upload-button"]);
    window.dispatchEvent(new Event("snaplist:research-capture"));
  };
  const tipMsg = () => {
    const tip = PAGE_TIPS[pathname];
    const ids = (tip?.ids || []).filter((id) => topics.some((t) => t.id === id));
    return tip && ids.length ? { from: "edgar", tip: pathname, text: `On ${tip.name}, people often ask:`, more: ids } : null;
  };
  const openEdgar = () => {
    const m = tipMsg();
    if (m && chat[chat.length - 1]?.tip !== pathname) setChat((c) => [...c, m]);
    setOpen(true);
  };
  const topic = (id) => topics.find((t) => t.id === id);

  // Hands-free: spoken questions, spoken answers, voice commands and the "Hey Edgar" wake word.
  const [prefs, setPrefsRaw] = useEdgarPrefs();
  const setPrefs = (p) => { setPrefsRaw(p); if (!p.on) hush(); };
  const [listening, setListening] = useState(false);
  const rec = useRef(null);
  const lastRun = useRef(null);
  const live = useRef({});
  const stopVoice = () => { try { rec.current?.stop(); } catch { /* noop */ } rec.current = null; setListening(false); hush(); };
  const say = (msg, then) => (live.current.prefs.on || live.current.pocket ? speak(msg, then) : then?.());
  // Headphone mode: always listening with beep cues, screen kept awake, auto-off after 3 quiet minutes.
  const [pocket, setPocket] = useState(false);
  const heardAt = useRef(0);
  const awaitingItem = useRef(false);
  const listen = (cue = true) => {
    if (!voiceSupported || rec.current) return;
    hush();
    setListening(true);
    if (cue && live.current.pocket) beep(true);
    rec.current = listenOnce((heard) => { heardAt.current = Date.now(); live.current.onVoice(heard); }, (heard) => {
      rec.current = null;
      setListening(false);
      if (!heard && live.current.pocket) setTimeout(() => live.current.pocket && listen(false), 300);
    });
  };
  const startPocket = () => {
    heardAt.current = Date.now();
    setPocket(true);
    live.current.pocket = true;
    keepAwake(true);
    setOpen(false);
    speak("Headphone mode on. Keep the screen on and the phone in your pocket. Say what's this worth and the item's name.", () => setTimeout(listen, 250));
  };
  const stopPocket = (auto) => {
    setPocket(false);
    live.current.pocket = false;
    awaitingItem.current = false;
    keepAwake(false);
    stopVoice();
    beep(false);
    speak(auto ? "It's been quiet for a while, so I turned off headphone mode to save battery." : "Headphone mode off.");
  };
  useEffect(() => {
    if (!pocket) return undefined;
    const t = setInterval(() => { if (Date.now() - heardAt.current > POCKET_IDLE_MS) stopPocket(true); }, 10000);
    return () => clearInterval(t);
  }, [pocket]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const on = () => (live.current.pocket ? stopPocket(false) : startPocket());
    window.addEventListener("snaplist:edgar-pocket", on);
    return () => window.removeEventListener("snaplist:edgar-pocket", on);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const researchByName = (name) => {
    awaitingItem.current = false;
    sessionStorage.setItem("snaplist.edgarSay", name);
    visit("/research", "single", []);
    window.dispatchEvent(new Event("snaplist:research-say"));
    say(`Checking ${name}.`);
  };
  const again = () => { if (live.current.pocket || (live.current.prefs.on && live.current.prefs.loop && live.current.open)) setTimeout(listen, 250); };
  const onVoice = (heard) => {
    const cmd = command(heard);
    const last = [...chat].reverse().find((m) => m.id);
    if (cmd === "pocket") { if (!live.current.pocket) startPocket(); else say("Headphone mode is already on.", again); return; }
    if (cmd === "pocket-off" || (cmd === "stop" && live.current.pocket)) { stopPocket(false); return; }
    if (awaitingItem.current && !cmd) { researchByName(heard.trim()); return; }
    const named = itemName(heard);
    if (named && !/\b(something|anything|stuff|items?|things?|how)\b/.test(named) && (live.current.pocket || /\bworth\b/i.test(heard))) { researchByName(named); return; }
    if (cmd === "worth" && live.current.pocket) {
      awaitingItem.current = true;
      say("What is it?", again);
      return;
    }
    if (cmd === "stop") { setChat((c) => [...c, { from: "you", text: heard }]); stopVoice(); return; }
    if (cmd === "close") { stopVoice(); setOpen(false); return; }
    if (cmd === "status") {
      api.get("/keepers/today", { params: { tz_offset: new Date().getTimezoneOffset() } })
        .then(({ data }) => { const msg = statusSpeech(data); setChat((c) => [...c, { from: "you", text: heard }, { from: "edgar", text: msg }]); say(msg, again); })
        .catch(() => say("I couldn't load your keepers right now.", again));
      return;
    }
    if (cmd === "price") {
      const run = lastRun.current;
      if (!run) { say("Research it first, then tell me what they want for it.", again); return; }
      api.get("/research/settings", { swr: true }).then(({ data }) => {
        const s = { ...DEFAULT_FLIP, ...(data.flip || {}) };
        const msg = priceSpeech(flipMath(run.suggested_price, shipCost(run, s), s, askPrice(heard)));
        setChat((c) => [...c, { from: "you", text: heard }, { from: "edgar", text: msg }]);
        say(msg, again);
      }).catch(() => say("I couldn't check that price right now.", again));
      return;
    }
    if (cmd === "keep" || cmd === "skip") {
      if (pathname !== "/research") { say("Research something first, then say keep it or skip it.", again); return; }
      if (cmd === "keep" && team.researchOnly) { say("Keepers aren't on your plan, so I'll skip it.", again); return; }
      window.dispatchEvent(new Event(`snaplist:research-${cmd}`));
      say(cmd === "keep" ? "Kept. Say what's this worth for the next one." : "Skipped. Say what's this worth for the next one.", again);
      return;
    }
    if (cmd === "worth" || cmd === "scan") { say(cmd === "scan" ? "Opening the barcode scanner." : "Let's check it. Snap a photo of the item.", () => researchNow(cmd === "scan" ? "scan" : "photo")); return; }
    if (walk && ["next", "back", "end"].includes(cmd)) {
      const n = WALKS[walk.id].steps.length;
      if (cmd === "end" || (cmd === "next" && walk.i + 1 >= n)) { endWalk(cmd !== "end"); return; }
      stepTo(walk.id, cmd === "next" ? walk.i + 1 : Math.max(0, walk.i - 1)); return;
    }
    if (cmd === "reset") { setChat([HELLO, tipMsg()].filter(Boolean)); say("Okay, new chat.", again); return; }
    if (cmd === "go") {
      if (last && topic(last.id)) { say("Here you go.", () => go(topic(last.id))); return; }
      say("Ask me something first, then say take me there.", again); return;
    }
    const talkBack = () => { if (live.current.open || live.current.pocket) setTimeout(listen, 250); };
    ask(heard, { voice: true, onSpoken: talkBack }).then((reply) => {
      if (!reply || reply.spoken) return;
      if (reply.ai) { talkBack(); return; }
      say(reply.confirm ? `${reply.text} Say yes or no.` : reply.id ? `${reply.text} Say take me there to open it.` : reply.more ? "I'm not sure about that one yet. Try asking another way." : reply.text, talkBack);
    });
  };
  Object.assign(live.current, { onVoice, prefs, open, pocket });
  useEffect(() => {
    const onResult = (e) => {
      lastRun.current = e.detail || null;
      if (!(live.current.prefs.on || live.current.pocket) || !live.current.prefs.enabled || !e.detail) return;
      speak(resultSpeech(e.detail, !team.researchOnly), () => setTimeout(listen, 250));
    };
    window.addEventListener("snaplist:research-result", onResult);
    return () => window.removeEventListener("snaplist:research-result", onResult);
  }, [team.researchOnly]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (!open && !walk && !live.current.pocket) stopVoice(); }, [open]); // eslint-disable-line react-hooks/exhaustive-deps
  // The mic only starts on its own when the browser already allows it; otherwise one tap on the badge starts it, so opening the app never prompts.
  const [wakeArmed, setWakeArmed] = useState(() => sessionStorage.getItem("snaplist.wakeArmed") === "1");
  const arm = (on) => { setWakeArmed(on); sessionStorage.setItem("snaplist.wakeArmed", on ? "1" : "0"); };
  useEffect(() => {
    if (!prefs.wakeOn || sessionStorage.getItem("snaplist.wakeArmed") === "0") return;
    navigator.permissions?.query({ name: "microphone" }).then((p) => { if (p.state === "granted") setWakeArmed(true); }).catch(() => {});
  }, [prefs.wakeOn]);
  const wakeActive = voiceSupported && prefs.enabled && prefs.wakeOn && wakeArmed && !open && !walk && !pocket;
  useEffect(() => {
    if (!wakeActive) return undefined;
    return wakeListener(() => { live.current.wake(); }, () => arm(false));
  }, [wakeActive]); // eslint-disable-line react-hooks/exhaustive-deps
  live.current.wake = () => { openEdgar(); setTimeout(() => speak("I'm listening.", () => setTimeout(listen, 150)), 300); };
  useEffect(() => {
    const onStart = () => speak("Ready when you are. Hold up an item and say, what's this worth.", () => setTimeout(listen, 250));
    window.addEventListener("snaplist:edgar-start", onStart);
    return () => window.removeEventListener("snaplist:edgar-start", onStart);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (!prefs.enabled) { setOpen(false); setWalk(null); stopVoice(); } }, [prefs.enabled]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!prefs.enabled) return null;
  return (
    <>
      {walk && (
        <WalkBar walk={WALKS[walk.id]} step={walk.i} onBack={() => stepTo(walk.id, Math.max(0, walk.i - 1))} onEnd={() => endWalk(false)}
          onNext={() => (walk.i + 1 >= WALKS[walk.id].steps.length ? endWalk(true) : stepTo(walk.id, walk.i + 1))} />
      )}
      {pocket && (
        <div className="fixed left-1/2 top-2 z-[62] flex -translate-x-1/2 items-center gap-2 rounded-full bg-slate-900 py-1.5 pl-3 pr-1.5 text-xs font-semibold text-white shadow-lg" data-testid="edgar-pocket-bar">
          <span className={`h-2 w-2 rounded-full ${listening ? "animate-pulse bg-rose-500" : "bg-emerald-400"}`} />
          <Headphones className="h-3.5 w-3.5" /> Headphone mode · {listening ? "listening" : "talking"}
          <button type="button" onClick={() => stopPocket(false)} className="rounded-full bg-white/15 px-2.5 py-1 hover:bg-white/25" data-testid="edgar-pocket-stop">Stop</button>
        </div>
      )}
      {!open && !walk && <EdgarButton onOpen={openEdgar} />}
      {voiceSupported && prefs.wakeOn && !open && !walk && !pocket && <WakeMic active={wakeActive} onToggle={() => arm(!wakeArmed)} />}
      {open && (
        <section className="fixed inset-x-2 bottom-2 z-50 flex max-h-[80vh] flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white shadow-2xl sm:inset-x-auto sm:bottom-6 sm:right-6 sm:w-[380px]" style={{ maxHeight: "min(80vh, calc(100dvh - 1rem), 680px)" }} data-testid="edgar-panel">
          <header className="flex shrink-0 items-center gap-2.5 border-b border-slate-100 px-4 py-3">
            <Avatar />
            <div className="min-w-0 flex-1">
              <p className="text-sm font-bold text-slate-900">Edgar</p>
              <p className="text-[11px] text-slate-500">Your SnapList guide</p>
            </div>
            <button type="button" onClick={() => setChat([HELLO, tipMsg()].filter(Boolean))} className="rounded-full px-2 py-1 text-[11px] font-semibold text-slate-500 hover:bg-slate-100" data-testid="edgar-reset">New chat</button>
            <button type="button" onClick={() => setOpen(false)} aria-label="Close Edgar" className="rounded-full p-1 text-slate-500 hover:bg-slate-100" data-testid="edgar-close"><X className="h-4 w-4" /></button>
          </header>
          <VoiceSettings prefs={prefs} setPrefs={setPrefs} supported={voiceSupported} />
          <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-3" data-testid="edgar-messages">
            {chat.map((m, n) => (
              <div key={n} className={`flex ${m.from === "you" ? "justify-end" : "gap-2"}`} data-testid={`edgar-msg-${n}`}>
                {m.from === "edgar" && <Avatar size="h-6 w-6" />}
                <div className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm ${m.from === "you" ? "rounded-br-sm bg-[#0053DA] text-white" : "rounded-tl-sm bg-slate-100 text-slate-800"}`}>
                  {m.thinking ? <p className="flex gap-1 py-1" data-testid="edgar-thinking" aria-label="Edgar is thinking">{[0, 1, 2].map((d) => <span key={d} className="h-1.5 w-1.5 animate-bounce rounded-full bg-slate-400" style={{ animationDelay: `${d * 120}ms` }} />)}</p> : <p>{m.text}</p>}
                  {m.buy && (
                    <button type="button" onClick={() => buyPack("answers")} data-testid="edgar-buy-more" className="mt-2 rounded-full bg-[#0053DA] px-3 py-1 text-xs font-semibold text-white hover:bg-[#0041B0]">Add 100 answers · $1.99</button>
                  )}
                  {m.confirm && (
                    <span className="mt-2 flex gap-2">
                      <button type="button" onClick={() => ask("yes")} data-testid="edgar-do-it" className="rounded-full bg-[#0053DA] px-3 py-1 text-xs font-semibold text-white hover:bg-[#0041B0]">Do it</button>
                      <button type="button" onClick={() => ask("no")} data-testid="edgar-not-now" className="rounded-full border border-slate-300 px-3 py-1 text-xs font-semibold text-slate-700 hover:bg-slate-50">Not now</button>
                    </span>
                  )}
                  {m.id && topic(m.id) && (
                    <button type="button" onClick={() => go(topic(m.id))} data-testid={topic(m.id).walk ? `edgar-walk-start-${m.id}` : `edgar-go-${m.id}`}
                      className="mt-2 inline-flex items-center gap-1 rounded-full bg-slate-900 px-3 py-1 text-xs font-semibold text-white transition-colors hover:bg-[#0053DA]">
                      {topic(m.id).walk ? "Start walkthrough" : "Take me there"} <ArrowRight className="h-3 w-3" />
                    </button>
                  )}
                  {m.more?.length > 0 && (
                    <div className="mt-2 flex flex-wrap gap-1">
                      {m.more.map((id) => topic(id) && (
                        <button key={id} type="button" onClick={() => pick(id)} data-testid={`edgar-suggest-${n}-${id}`}
                          className="rounded-full border border-slate-300 bg-white px-2 py-0.5 text-left text-[11px] text-slate-700 hover:border-[#0053DA] hover:text-[#0053DA]">{topic(id).q}</button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}
            {chat.length === 1 && (
              <div className="flex flex-wrap gap-1.5 pl-8" data-testid="edgar-starters">
                {STARTERS.map((id) => topic(id) && (
                  <button key={id} type="button" onClick={() => pick(id)} data-testid={`edgar-starter-${id}`}
                    className="rounded-full border border-slate-300 px-2.5 py-1 text-left text-xs text-slate-700 transition-colors hover:border-[#0053DA] hover:text-[#0053DA]">{topic(id).q}</button>
                ))}
              </div>
            )}
            <div ref={end} />
          </div>
          <form onSubmit={(e) => { e.preventDefault(); ask(text); }} className="flex shrink-0 items-center gap-2 border-t border-slate-100 p-2.5">
            <input value={text} onChange={(e) => setText(e.target.value)} maxLength={200} placeholder={listening ? "Listening… say your question" : "Ask Edgar, e.g. how do I merge batches?"} data-testid="edgar-input"
              className="h-10 min-w-0 flex-1 rounded-full border border-slate-300 px-4 text-base sm:text-sm" />
            {voiceSupported && (
              <button type="button" onClick={() => (listening ? stopVoice() : listen())} aria-label={listening ? "Stop listening" : "Speak your question"} aria-pressed={listening} data-testid="edgar-mic"
                className={`relative inline-flex h-10 w-10 items-center justify-center rounded-full transition-colors ${listening ? "bg-rose-600 text-white" : "border border-slate-300 text-slate-700 hover:border-[#0053DA] hover:text-[#0053DA]"}`}>
                {listening && <span className="absolute inset-0 animate-ping rounded-full bg-rose-500/40" />}
                <Mic className="relative h-4 w-4" />
              </button>
            )}
            <button type="submit" disabled={!text.trim()} aria-label="Send" data-testid="edgar-send"
              className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#0053DA] text-white transition-colors hover:bg-[#0041B0] disabled:opacity-40"><Send className="h-4 w-4" /></button>
          </form>
        </section>
      )}
    </>
  );
}
